import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './filter-profile.module.scss';
import { Button, Radio, RangeSlider, Select, Typography } from '@/ui-kit';
import { FormProvider, useForm } from 'react-hook-form';
import { useGetRegions } from '@/hooks/use-get-regions';
import { filterProfileSchema, FilterProfileSchemaType } from '@/schemes/filter-profile-schema';
import { Region, SearchParamsRegions } from '@/api/region/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { Profile } from '@/types/profile';
import { debounce, isArray } from 'lodash';
import { useUpdateUserData } from '@/hooks/use-update-profile';
import { useQueryClient } from '@tanstack/react-query';
import { renderCity } from '@/utils/render-city';
import { ANY_REGION_OPTION, ANY_REGION_OPTION_LABEL } from '@/utils/regions';
import { SelectOption } from '@/ui-kit/select';
import { SizedBox } from '@/ui-kit/sized-box';
import { ExpandedSearch } from '../expanded-search';
import { useFilter } from '@/store/use-filter';
import { useProfilesEndedEvent } from '@/store/useProfilesEndedEvent';
import { useTranslation } from 'react-i18next';

interface Props {
    userData: Profile;
    onCloseModal?: () => void;
}

const DELAY_SEND_DATA = 500;

export const FilterProfile: React.FC<Props> = ({ userData, onCloseModal }) => {
    const { mutateAsync: updateUserData, isPending } = useUpdateUserData();
    const queryClient = useQueryClient();
    const [seatchParamsCities, setSeatchParamsCities] = useState<SearchParamsRegions>({ q: '' });
    const [search, setSearch] = useState<string>('');

    const [isDisabled, setDisabled] = useState<boolean>(true)

    useEffect(() => {
        setDisabled(
            methods
                .getValues('regions')
                .length === 0
        )
    }, [])

    const { isLoading: isLoadingCities, data: dataRegions } = useGetRegions(seatchParamsCities);

    const { t } = useTranslation();

    const localizeAnyRegion = {
        ...ANY_REGION_OPTION,
        label: t(ANY_REGION_OPTION_LABEL)
    }
    
    const defaultValues = useMemo(() => {
        const getMinAge = () => {
            return userData.min_age !== null ? (userData.min_age >= 60 ? 60 : userData.min_age) : 18;
        };

        const getMaxAge = () => {
            return userData.max_age !== null ? (userData.max_age >= 60 ? 60 : userData.max_age) : 24;
        };
        return {
            looks_for: userData?.looks_for ?? 'any',
            region: userData?.region ? { label: renderCity(userData.region), value: userData.region.id } : undefined,
            regions: userData?.regions
                ? (
                    userData.regions.length ?
                        userData.regions.map((region) => ({
                            label: renderCity(region),
                            value: region.id,
                        }))
                    : [localizeAnyRegion]
                )
                : [localizeAnyRegion],
            min_age: getMinAge(),
            max_age: getMaxAge(),
            expanded_age_search: userData.expanded_age_search ?? false
        };
    }, [userData]);

    // init expanded search
    useEffect(
        () => {
            if (
                filterStore
                    .isExpandedSearch
                    === null
            )
                filterStore
                    .setExpandedSearch(
                        defaultValues.expanded_age_search
                    )
        },
        [userData]
    )

    const [minAge, setMinAge] = useState(defaultValues.min_age);
    const [maxAge, setMaxAge] = useState(defaultValues.max_age);
    const filterStore = useFilter()
    const { setFiltersWasApplied } =
        useProfilesEndedEvent(
            (state) => state
        );

    const methods = useForm<FilterProfileSchemaType>({
        resolver: zodResolver(filterProfileSchema),
        defaultValues,
    });

    const {
        register,
        handleSubmit,
        formState: { errors, isDirty, isValid },
    } = methods;
    const handleChangeRangeAge = (value: any) => {
        methods.setValue('min_age', value[0]);
        methods.setValue('max_age', value[1]);
        setMinAge(value[0]);
        setMaxAge(value[1]);
    };

    const handleSearchCityDebounce = useCallback(
        (e: string) => {
            setSearch(e);
            (debounce((q: string) => {
                setSeatchParamsCities({ q: q.trim() });
            }, DELAY_SEND_DATA))(e);
        },
        [seatchParamsCities],
    );

    const onSubmit = async (data: FilterProfileSchemaType) => {
        const minAgeToSend = data.min_age >= 60 ? 100 : data.min_age;
        const maxAgeToSend = data.max_age >= 60 ? 100 : data.max_age;
        console.log('DDD', data);
        let regionIds =
            data
                .regions
                .map((item: { value: any }) => item.value)
        if (
            regionIds
                .includes(
                    ANY_REGION_OPTION.value
                )
        )
            regionIds = []
        await updateUserData({
            looks_for: data.looks_for,
            min_age: minAgeToSend,
            max_age: maxAgeToSend,
            region_ids: regionIds,
            ...(
                filterStore.isExpandedSearch !== null ?
                    {
                        expanded_age_search: filterStore.isExpandedSearch,
                    }
                :
                    {}
            ),
        });
        queryClient.invalidateQueries({ queryKey: ['profiles'] });
        onCloseModal?.();
        setFiltersWasApplied(true)
    };

    return (
        <div className={styles.wrapper}>
            <Button
                className={styles.buttonReset}
                onClick={() => {
                    methods.reset();
                    setMinAge(defaultValues.min_age);
                    setMaxAge(defaultValues.max_age);
                }}>
                {t('browse.clear')}
            </Button>
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Typography
                        typeColor="gray"
                        fontFamily="SFProText"
                        className={styles.label}>
                        {t('who_are_you_looking_for')}
                    </Typography>
                    <div className={styles.formRadioGroup}>
                        <Radio
                            label={t('male_secondary')}
                            value="male"
                            {...register('looks_for')}
                            isTypeButton />
                        <Radio
                            label={t('female_secondary')}
                            value="female"
                            {...register('looks_for')}
                            isTypeButton />
                        <Radio
                            label={t('optional')}
                            value="any"
                            {...register('looks_for')}
                            isTypeButton />
                    </div>
                    <Typography
                        typeColor="gray"
                        fontFamily="SFProText"
                        className={styles.label}>
                        {t('age_range')}
                    </Typography>
                    <RangeSlider
                        maxStart={maxAge}
                        minStart={minAge}
                        onChange={handleChangeRangeAge} />
                    <SizedBox height={15} />
                    <ExpandedSearch />
                    <SizedBox height={16} />
                    <Typography
                        typeColor="gray"
                        fontFamily="SFProText"
                        className={styles.label}>
                        {t('regions')}
                    </Typography>
                    {/* unset clearable for 14804 task */}
                    <Select
                        wrapperClassName={styles.selectWrap}
                        name="regions"
                        isLoading={isLoadingCities}
                        options={
                            [
                                ...search.length > 0 ? [] : [localizeAnyRegion],
                                ...search.length > 0 ?
                                    dataRegions?.data?.map((region: Region) => ({
                                        label: renderCity(region),
                                        value: region.id
                                    })) ?? []
                                : [],
                            ]
                        }
                        isMulti={true}
                        onSearch={handleSearchCityDebounce}
                        isClearable={false}
                        onSelected={(val, setState) => {
                            if (isArray(val)) {
                                if (val.length) {
                                    if (
                                        val[
                                            val.length - 1
                                        ].value === ANY_REGION_OPTION.value
                                    ) setState([localizeAnyRegion])
                                    else if (
                                        val.map(
                                            (e: SelectOption) => e.value
                                        )
                                            .includes(ANY_REGION_OPTION.value)
                                    )
                                        setState(
                                            val.filter(
                                                (e: SelectOption) =>
                                                    e.value !== ANY_REGION_OPTION.value
                                            )
                                        )
                                }
                                setDisabled(methods.getValues('regions').length === 0)
                            }
                        }} />
                    <Button
                        isDisabled={isDisabled}
                        typeColor="orange"
                        variant="stretched"
                        isLoading={isPending}>
                        {t('apply')}
                    </Button>
                </form>
            </FormProvider>
        </div>
    );
};
